import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getWorkspaceContext, requireWriteAccess, recordSessionActivity, getAgentSessionId } from "../client.js";
import { formatRubricCoaching } from "./smart-capture.js";
import { runConflictPreflight } from "./conflict-preflight.js";
import { trackQualityVerdict, trackQualityCheck, trackChainEntryCommitted, trackFieldQualityWarning, trackCommitErrorByCode } from "../analytics.js";
import { extractPreview, deriveEpistemicStatus, formatEpistemicLine, toEpistemicInput } from "./knowledge-helpers.js";
import { failureResult, success, notFoundResult, successResult, type NextAction, type CaptureContract, type ToolResult } from "../envelope.js";
// WP-465 surface parity (TEN-2458): extracted to coherency-refusal.ts to break the circular dep with smart-capture.ts.
import { coherencyRefusedResult, type CoherencyRefusalPayload } from "./coherency-refusal.js";
export { coherencyRefusedResult, type CoherencyRefusalPayload } from "./coherency-refusal.js";

// FROZEN HISTORICAL LITERAL — NOT a live mirror; do NOT add values here (WP-480 S2).
// This array survives only to back the BET-68 legacy `status`→`workflowStatus` shim
// (WorkflowStatus type + LEGACY_WORKFLOW_STATUSES routing below): it covers exactly
// the workflow values that existed when the deprecation was promised, so it cannot
// meaningfully drift. The live vocabulary is server-owned per collection
// (`collections action=describe` serves it; the server rejects invalid values and
// returns the valid set). At the shim's sunset (~2026-09-03) delete this array with it.
export const WORKFLOW_STATUS_VALUES = [
  // Generic / governed collections
  'open', 'pending', 'proposed', 'accepted', 'needs-amendment',
  'withdrawn', 'review', 'in-progress', 'decided', 'conflict',
  'processing', 'closed',
  // Chains (bet type) lifecycle
  'shaped', 'bet', 'building', 'shipped',
  // Assumptions lifecycle
  'untested', 'testing', 'validated', 'invalidated',
  // Insights lifecycle
  'hypothesis', 'evidenced',
] as const;

type WorkflowStatus = typeof WORKFLOW_STATUS_VALUES[number];

type LifecycleStatus = 'draft' | 'active' | 'deprecated' | 'archived';

// Backward-compat shim (BET-68, expires ~2026-09-03)
// Workflow values passed via `status` are routed to `workflowStatus` with a deprecation warning.
const LEGACY_WORKFLOW_STATUSES = new Set<string>(WORKFLOW_STATUS_VALUES);

export const updateEntrySchema = z.object({
  entryId: z.string().describe("Entry ID to update, e.g. 'T-SUPPLIER', '<PREFIX>-<n>'"),
  name: z.string().optional().describe("New display name"),
  status: z.union([
    z.enum(['draft', 'active', 'deprecated', 'archived']),
    // BET-68 legacy shim: frozen historical workflow values still pass through
    // `status` (auto-routed with a warning) until the ~2026-09-03 sunset.
    z.enum(WORKFLOW_STATUS_VALUES),
  ]).optional().describe("Lifecycle status: draft | active | deprecated | archived. **Workflow values are deprecated here — use `workflowStatus` instead. Passing a workflow value as `status` will be auto-routed with a warning until 2026-09-03, then hard-errored.**"),
  workflowStatus: z.string().optional().describe("Collection workflow state. Valid values are collection-specific and server-owned — discover them via `collections action=describe` for the target collection. The server rejects invalid values and returns the valid set in the error."),
  data: z.record(z.unknown()).optional().describe("Fields to update (merged with existing data)"),
  order: z.number().optional().describe("New sort order"),
  canonicalKey: z.string().optional().describe("Semantic type (e.g. 'decision', 'tension'). Only changeable on draft/uncommitted entries."),
  autoPublish: z.boolean().optional().default(false).describe("Only true when user explicitly asks to publish. Default false = draft. Never auto-publish without user confirmation."),
  changeNote: z.string().optional().describe("Strongly recommended: short human-readable rationale for WHY this change was made (e.g. 'Aligned description with F1-themed copy'). Surfaces in activity feed and pb get. If omitted, falls back to session purpose or auto-generated field summary."),
  sourceRef: z.string().optional().describe("URI or path of the source document backing this entry. Write-once: can only be set if currently empty."),
  sourceExcerpt: z.string().optional().describe("Verbatim excerpt from the source that backs this entry's claims. Write-once: can only be set if currently empty."),
  // WP-465 slice ⑤ — relay-only (TEN-2233): validation/min-length/recording live in Convex.
  steeringOverrideReason: z.string().optional().describe("Typed override (≥12 chars) that clears a steering coherency block on a misaligned governance write — always recorded with author attribution."),
  coherencyAcknowledgement: z.object({
    response: z.enum(['linked', 'accepted-fix', 'diverged']).describe("Explicit response to an acknowledge-required coherency challenge."),
    entryId: z.string().optional().describe("Authorizing entry being linked/accepted (required for 'linked' and 'accepted-fix')."),
    reason: z.string().optional().describe("Divergence rationale (required for 'diverged', ≥12 chars)."),
  }).optional().describe("Explicit response to a coherency challenge (standard/strict workspace modes). One acknowledgement per challenge per entry per session."),
});


export const getHistorySchema = z.object({
  entryId: z.string().describe("Entry ID, e.g. 'T-SUPPLIER', '<PREFIX>-<n>'"),
});

export const commitEntrySchema = z.object({
  entryId: z.string().describe("Entry ID to accept, e.g. 'TEN-abc123', '<PREFIX>-<n>'"),
  // WP-316 S3: Preview gate — dry-run mode. Returns would-succeed result, no DB writes.
  preview: z.boolean().optional().describe("If true, validates the accept without writing. Returns what would happen. Default false."),
  // WP-465 slice ⑤ — relay-only (TEN-2233): validation/recording live in Convex.
  steeringOverrideReason: z.string().optional().describe("Typed override (≥12 chars) that clears a steering coherency block at the publish chokepoint — always recorded with author attribution."),
  coherencyAcknowledgement: z.object({
    response: z.enum(['linked', 'accepted-fix', 'diverged']).describe("Explicit response to an acknowledge-required coherency challenge."),
    entryId: z.string().optional().describe("Authorizing entry being linked/accepted (required for 'linked' and 'accepted-fix')."),
    reason: z.string().optional().describe("Divergence rationale (required for 'diverged', ≥12 chars)."),
  }).optional().describe("Explicit response to a coherency challenge (standard/strict workspace modes)."),
});

/**
 * WP-484 S1 (§3/§4 entries-family migration): update-entry's handler body,
 * extracted so BOTH the legacy `update-entry` tool AND the new
 * `entries action="update"` variant call the SAME implementation — one
 * business-logic path. WP-484 S3 (clean break, TEN-2157): the standalone
 * `update-entry` tool retired — `entries` (action=update) is the only
 * remaining caller, so `toolName` is always "entries" now. The parameter
 * stays (rather than hardcoding the literal inline) so a future caller with
 * a different self-reference doesn't have to plumb a new argument through.
 */
export async function handleUpdateEntry(
  { entryId, name, status: rawStatus, workflowStatus: rawWorkflowStatus, data, order, canonicalKey, autoPublish, changeNote, sourceRef, sourceExcerpt, steeringOverrideReason, coherencyAcknowledgement }: z.infer<typeof updateEntrySchema>,
  toolName: "entries" = "entries",
): Promise<ToolResult> {
      requireWriteAccess();

      const fieldsProvided: string[] = [];
      if (name !== undefined) fieldsProvided.push('name');
      if (rawStatus !== undefined) fieldsProvided.push('status');
      if (rawWorkflowStatus !== undefined) fieldsProvided.push('workflowStatus');
      if (data !== undefined) fieldsProvided.push('data');
      if (order !== undefined) fieldsProvided.push('order');
      if (canonicalKey !== undefined) fieldsProvided.push('canonicalKey');

      let status: LifecycleStatus | undefined = rawStatus as LifecycleStatus | undefined;
      let workflowStatus: WorkflowStatus | undefined = rawWorkflowStatus as WorkflowStatus | undefined;
      let deprecationWarning: string | undefined;

      if (rawStatus && LEGACY_WORKFLOW_STATUSES.has(rawStatus)) {
        if (!workflowStatus) {
          workflowStatus = rawStatus as WorkflowStatus;
        }
        status = undefined;
        deprecationWarning = `⚠️ Deprecation: \`status: '${rawStatus}'\` — use \`workflowStatus: '${rawStatus}'\` instead. Support ends ~2026-09-03.`;
      }

      // BET-192 / FEAT-575: chain.updateEntry now returns warnings + normalization breakdown
      const updateResult = await kernelMutation<{
        id: string;
        warnings: string[];
        normalization: { remapped: Record<string, string>; rejected: string[]; accepted: string[] };
        normalizationWarnings: string[];
        validationWarnings: string[];
      }>("chain.updateEntry", {
        entryId,
        name,
        status,
        workflowStatus,
        data,
        order,
        canonicalKey,
        autoPublish,
        changeNote,
        changedBy: getAgentSessionId() ? `agent:${getAgentSessionId()}` : undefined,
        ...(sourceRef ? { sourceRef } : {}),
        ...(sourceExcerpt ? { sourceExcerpt } : {}),
        ...(steeringOverrideReason ? { steeringOverrideReason } : {}),
        ...(coherencyAcknowledgement ? { coherencyAcknowledgement } : {}),
      });

      // WP-465 slice ⑤ (Option B): a refused write RETURNS the structured payload —
      // render it as a failure with the routes; never as a silent success.
      const maybeRefused = updateResult as unknown as { outcome?: string; refusal?: CoherencyRefusalPayload };
      if (maybeRefused.outcome === 'refused' && maybeRefused.refusal) {
        return coherencyRefusedResult(`${toolName} action=update`, entryId, maybeRefused.refusal);
      }

      const id = updateResult.id;
      await recordSessionActivity({ entryModified: id });

      const wsCtx = await getWorkspaceContext();
      const updated = await kernelQuery<any>("chain.getEntry", { entryId });
      const entryStatus: LifecycleStatus = (updated?.status ?? 'draft') as LifecycleStatus;
      const versionMode = entryStatus === 'active' ? 'published' : `saved as ${entryStatus}`;
      const responseLines = [
        `# Entry Updated`,
        ``,
        `**${entryId}** — ${versionMode}.`,
        ``,
        `Internal ID: ${id}`,
        `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
      ];

      if (fieldsProvided.length > 0) {
        responseLines.push(`**Fields provided:** ${fieldsProvided.join(', ')}`);
      } else {
        responseLines.push('');
        responseLines.push('⚠️ No fields to update were provided — only `entryId` was set.');
      }

      if (deprecationWarning) {
        responseLines.push('');
        responseLines.push(deprecationWarning);
      }

      // BET-192 / FEAT-575: Surface normalization warnings (fields silently dropped by normalizeEntryData)
      if (updateResult.normalizationWarnings.length > 0) {
        responseLines.push('');
        responseLines.push('⚠️ **Normalization warnings:**');
        for (const warning of updateResult.normalizationWarnings) {
          responseLines.push(`  - ${warning}`);
        }
      }

      // BET-192 / FEAT-575: Surface validation warnings (missing required fields, etc.)
      if (updateResult.validationWarnings.length > 0) {
        responseLines.push('');
        responseLines.push('⚠️ **Validation warnings:**');
        for (const warning of updateResult.validationWarnings) {
          responseLines.push(`  - ${warning}`);
        }
      }

      const summary = `Updated ${entryId} — ${versionMode}. Fields: ${fieldsProvided.join(', ') || 'none'}.`;
      const next: NextAction[] = [
        { tool: toolName, description: "View updated entry", parameters: { action: "get", entryId } },
        { tool: toolName, description: "Accept onto Chain", parameters: { action: "commit", entryId } },
      ];

      return {
        content: [{ type: "text" as const, text: responseLines.join("\n") }],
        structuredContent: success(summary, {
          entryId: id,
          versionMode,
          fieldsProvided,
          // BET-192: normalization breakdown in structuredContent — symmetric with capture tool
          ...(updateResult.normalization && (Object.keys(updateResult.normalization.remapped).length > 0 || updateResult.normalization.rejected.length > 0) && {
            normalization: {
              remapped: updateResult.normalization.remapped,
              rejected: updateResult.normalization.rejected,
            },
          }),
          ...(updateResult.normalizationWarnings.length > 0 && {
            normalizationWarnings: updateResult.normalizationWarnings,
          }),
          ...(updateResult.validationWarnings.length > 0 && {
            validationWarnings: updateResult.validationWarnings,
          }),
        }, next),
      };
}

/**
 * WP-484 S1: get-history's handler body, extracted so both `get-history` and
 * `entries action="history"` share it.
 */
export async function handleGetHistory({ entryId }: z.infer<typeof getHistorySchema>): Promise<ToolResult> {
      const history = await kernelQuery<any[]>("chain.listEntryHistory", { entryId });

      if (history.length === 0) {
        return successResult(
          `No history found for ${entryId}.`,
          `No history events recorded for ${entryId}.`,
          { entryId, eventCount: 0, events: [] },
        );
      }

      const formatted = history
        .map((h) => {
          const date = new Date(h.timestamp).toISOString();
          const changes = h.changes ? ` — ${JSON.stringify(h.changes)}` : "";
          // WP-D (attestation spec §7.2): server-derived qualifier beside legacy 'verified'
          // events — the ledger sentence stays as written, the display stops lying.
          const qualifier = h.legacyQualifier ? ` [${h.legacyQualifier}]` : "";
          return `- **${date}** ${h.event}${h.changedBy ? ` _(${h.changedBy})_` : ""}${changes}${qualifier}`;
        })
        .join("\n");

      return {
        content: [{ type: "text" as const, text: `# History for \`${entryId}\` (${history.length} events)\n\n${formatted}` }],
        structuredContent: success(
          `Found ${history.length} history events for ${entryId}.`,
          { entryId, eventCount: history.length, events: history },
        ),
      };
}

/**
 * WP-484 S1/S3: commit-entry's handler body. WP-484 S3 (clean break,
 * TEN-2157): the standalone `commit-entry` tool retired — `entries`
 * (action=commit) is the only remaining caller, same `toolName` convention
 * as `handleUpdateEntry`.
 */
export async function handleCommitEntry(
  { entryId, preview, steeringOverrideReason, coherencyAcknowledgement }: z.infer<typeof commitEntrySchema>,
  toolName: "entries" = "entries",
): Promise<ToolResult> {
      requireWriteAccess();

      const entry = await kernelQuery<any>("chain.getEntry", { entryId });
      if (!entry) {
        return notFoundResult(entryId, `Entry '${entryId}' not found. Try search to find the right ID.`);
      }

      const descField = entry.data?.description ?? entry.data?.canonical ?? entry.data?.rationale ?? "";
      const preflight = await runConflictPreflight(
        entry.name,
        descField,
        entry.collectionSlug ?? entry.collection?.slug ?? entry.collection?.name,
      );
      const advisoryWarnings = preflight.source === "semantic"
        ? preflight.conflicts
            .filter((conflict) => !preflight.blockingContradictions.includes(conflict))
            .map((conflict) => ({
              kind: "semantic" as const,
              entryId: conflict.chainEntryId,
              name: conflict.chainEntryName,
              explanation: conflict.explanation,
            }))
        : preflight.advisoryWarnings.map((warning) => ({
            kind: "heuristic" as const,
            entryId: warning.entryId,
            name: warning.name,
            collection: warning.collection,
            governsCount: warning.governsCount,
          }));

      if (preflight.blockingContradictions.length > 0) {
        await recordSessionActivity({ contradictionWarning: true });
        const conflictLines = preflight.blockingContradictions
          .map((c) => `- ${c.chainEntryName} (${c.chainEntryId}) — ${c.explanation}`)
          .join("\n");

        return failureResult(
          [
            `# Accept blocked: contradiction detected`,
            "",
            `\`${entryId}\` matches ${preflight.blockingContradictions.length} high-confidence contradiction(s). Resolve the conflict before accepting.`,
            "",
            conflictLines,
          ].join("\n"),
          "CONTRADICTION_BLOCKED",
          `${preflight.blockingContradictions.length} high-confidence contradiction(s) found.`,
          "Review the conflicting entry or entries before retrying the accept.",
          undefined,
          {
            entryId,
            source: preflight.source,
            blockingContradictions: preflight.blockingContradictions,
            advisoryWarnings,
          },
        );
      }

      if (advisoryWarnings.length > 0) {
        await recordSessionActivity({ contradictionWarning: true });
      }

      let coachingResult: any = null;
      // A REAL commit re-evaluates here so the gate (inside chain.commitEntry) reads a
      // fresh verdict for the current content — otherwise a stale passing row lets the
      // commit through on content that changed.
      //
      // Codex P1 (PR #291): a PREVIEW must NOT run this — it used to insert a throwaway
      // verdict row (so the preview probe would read a fresh judgment), but that row
      // polluted usage stats and became the entry's latest verdict, leaving it
      // un-promotable after a mere dry run. The preview now gets parity a different way:
      // chain.commitEntry's dry-run path computes the same verdict in memory and classifies
      // it (buildCommitPreviewVerdictRow → simulatedVerdictRow), writing nothing. So skip
      // the re-evaluate entirely on preview.
      if (!preview) {
        try {
          coachingResult = await kernelMutation<any>("quality.evaluateHeuristicAndSchedule", {
            entryId,
            context: "commit",
          });
        } catch {
          // Quality coaching is advisory
        }
      }

      let result: any;
      try {
        result = await kernelMutation<any>("chain.commitEntry", {
          entryId,
          author: getAgentSessionId() ? `agent:${getAgentSessionId()}` : undefined,
          sessionId: getAgentSessionId() ?? undefined,
          ...(preview ? { preview: true } : {}),
          ...(steeringOverrideReason ? { steeringOverrideReason } : {}),
          ...(coherencyAcknowledgement ? { coherencyAcknowledgement } : {}),
        });
      } catch (commitErr: unknown) {
        // WP-316 S1a: Fire PostHog event on structured validation failure before re-throwing.
        // The thinWrapper HOF will catch and format the error into diagnostics.
        const errCode = (commitErr as { code?: string })?.code;
        if (errCode === "VALIDATION_FAILED") {
          try {
            const wsCtx = await getWorkspaceContext();
            const e = commitErr as { missingRequiredFields?: string[]; fieldErrors?: string[] };
            trackCommitErrorByCode(wsCtx.workspaceId, {
              error_code: errCode,
              missing_field_count: e.missingRequiredFields?.length ?? 0,
              field_error_count: e.fieldErrors?.length ?? 0,
              entry_id: entryId,
            });
          } catch { /* tracking must not break commit error path */ }
        }
        throw commitErr;
      }

      // WP-465 slice ⑤ (Option B): a refused accept RETURNS the structured payload —
      // render it as a failure with the routes; never as a silent success.
      if (result?.status === 'coherency_refused' && result?.refusal) {
        return coherencyRefusedResult(`${toolName} action=commit`, entryId, result.refusal as CoherencyRefusalPayload);
      }

      // WP-316 S3: Preview gate — return dry-run result without recording activity.
      if (result?.preview) {
        const wouldBlock = result.wouldBlock === true;
        const previewEnvelope = success(
          `Preview: ${wouldBlock ? "would BLOCK accept" : "would accept"} ${entryId} — no DB writes`,
          {
            entryId,
            name: entry.name,
            outcome: wouldBlock ? ("blocked" as const) : ("preview" as const),
            currentStatus: result.currentStatus,
            wouldSetStatus: result.wouldSetStatus,
            wouldBlock,
            ...(wouldBlock && result.blockingReason ? { blockingReason: result.blockingReason } : {}),
          },
          [{ tool: toolName, description: "Accept for real", parameters: { action: "commit", entryId } }],
        );
        if (result.contract) {
          previewEnvelope.contract = result.contract;
        }
        const blockingNote = wouldBlock && result.blockingReason
          ? `\n\n**Would be blocked:** ${result.blockingReason}`
          : wouldBlock
            ? `\n\n**Would be blocked** by the coherency/grounding gate.`
            : "";
        return {
          content: [{ type: "text" as const, text: `# Preview: would ${wouldBlock ? "BLOCK accept" : "accept"} ${entryId}\n\nCurrent status: \`${result.currentStatus}\` → would become \`${result.wouldSetStatus}\`.${blockingNote}\n\nNo DB writes — call without \`preview:true\` to accept for real.` }],
          structuredContent: previewEnvelope,
        };
      }

      const docId = result?._id ?? entry._id;
      const wsCtx = await getWorkspaceContext();
      const isProposal = result?.status === "proposal_created";
      if (!isProposal) {
        await recordSessionActivity({ entryModified: docId });
        const coll =
          (entry.collectionSlug as string | undefined) ??
          (entry.collection?.slug as string | undefined);
        trackChainEntryCommitted(wsCtx.workspaceId, {
          entry_id: entry.entryId ?? entryId,
          collection: coll,
          commit_method: "manual",
          surface: "mcp_commit_tool",
        });
      }
      let lines: string[];

      if (isProposal) {
        lines = [
          `# Proposal created: ${result.entryId ?? entryId}`,
          `**${result.name ?? entry.name}** — accepting requires consent. A proposal was created instead of accepting directly.`,
          `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
          "",
          result.existing
            ? "An open proposal for this entry already exists."
            : "Affected owners will be notified. The entry will be accepted when the consent window expires with no objections, or when all owners approve.",
        ];
      } else {
        lines = [
          `# Accepted: ${entryId}`,
          `**${entry.name}** promoted to SSOT on the Chain.`,
          `**Workspace:** ${wsCtx.workspaceSlug} (${wsCtx.workspaceId})`,
        ];
        // WP-B (attestation spec §4.3): THE RECEIPT SAYS SO — the server attested this
        // entry as part of the promote (promote-gate context). Rendered, never re-derived.
        const autoVerified = result?.autoVerified as { strength?: string; verifiedBy?: string } | undefined;
        if (autoVerified?.strength) {
          const who = autoVerified.verifiedBy ? ` (${autoVerified.verifiedBy})` : "";
          lines.push("");
          lines.push(`_Auto-verified as part of promote — ${autoVerified.strength}${who}._`);
        }
      }

      if (advisoryWarnings.length > 0) {
        lines.push("");
        lines.push(preflight.source === "semantic"
          ? "⚠ Semantic conflict preflight: advisory conflict(s) found."
          : "⚠ Contradiction check: proposed entry matched existing governance entries:");
        for (const w of advisoryWarnings) {
          lines.push(w.kind === "semantic"
            ? `- ${w.name} (${w.entryId}) — ${w.explanation}`
            : `- ${w.name} (${w.collection}, ${w.entryId}) — has 'governs' relation to ${w.governsCount} entries`);
        }
        lines.push(preflight.source === "semantic"
          ? "Run context action=gather on the conflicting entries before accepting."
          : "Run context action=gather on these entries before accepting.");
      }

      if (coachingResult?.verdict) {
        try {
          const v = coachingResult.verdict;
          const failedCount = (v.criteria ?? []).filter((c: any) => !c.passed).length;
          trackQualityVerdict(wsCtx.workspaceId, {
            entry_id: entryId,
            entry_type: v.canonicalKey ?? entry.canonicalKey ?? "",
            tier: v.tier,
            context: "commit",
            passed: v.passed,
            source: coachingResult.source ?? "heuristic",
            criteria_total: v.criteria?.length ?? 0,
            criteria_failed: failedCount,
            llm_scheduled: v.tier !== "passive",
            methodology_version: v.methodologyVersion,
          });
        } catch { /* tracking is advisory */ }
      }

      if (coachingResult?.verdict && coachingResult.verdict.tier !== "passive" && coachingResult.verdict.criteria.length > 0) {
        const coaching = formatRubricCoaching(coachingResult);
        if (coaching) {
          lines.push("");
          lines.push(coaching);
          if (!coachingResult.verdict.passed && coachingResult.verdict.tier === "assertive") {
            lines.push("");
            lines.push("_This entry was accepted despite quality concerns. The override has been recorded._");
          }
        }
      }

      // BET-289 S5: Surface advisory guidance warnings from commit result + track
      const guidanceWarnings = result?.guidanceWarnings as Array<{ field: string; message: string; warningType: string }> | undefined;
      if (guidanceWarnings && guidanceWarnings.length > 0) {
        lines.push("");
        lines.push("## Field Guidance Warnings (advisory)");
        for (const w of guidanceWarnings) {
          lines.push(`- **${w.field}:** ${w.message}`);
        }
        lines.push(`_These are advisory — the entry was accepted. Use \`entries action=update entryId="${entryId}"\` to address them._`);

        const uniqueTypes = [...new Set(guidanceWarnings.map((w) => w.warningType))];
        trackFieldQualityWarning(wsCtx.workspaceId, {
          warning_count: guidanceWarnings.length,
          warning_types: uniqueTypes,
        });
      }

      // WP-465 slice ⑤ (Light-mode row-11): a non-blocking coherency advisory raised
      // when a misaligned governance draft is accepted. The accept proceeds; the
      // misalignment is surfaced alongside the other commit warnings, never enforced.
      const steeringAdvisory =
        typeof result?.steeringAdvisory === "string" && result.steeringAdvisory.trim()
          ? (result.steeringAdvisory as string)
          : undefined;
      if (steeringAdvisory) {
        lines.push("");
        lines.push(`⚠ Coherency advisory (entry accepted): ${steeringAdvisory}`);
      }

      // Epistemic advisory for insights without evidence (BR-85)
      const epistemic = deriveEpistemicStatus(toEpistemicInput(entry as Record<string, unknown>));

      if (epistemic && (epistemic.level === "hypothesis" || epistemic.level === "untested")) {
        lines.push("");
        lines.push(`⚠ ${formatEpistemicLine(epistemic)}`);
        if (epistemic.action) {
          lines.push(`  → ${epistemic.action}`);
        }
      }

      // WP-316 S1a: Fetch capture contract so agents know required fields after commit.
      // Advisory — failure must not block the commit response.
      let captureContract: CaptureContract | undefined;
      try {
        const collSlug =
          (entry.collectionSlug as string | undefined) ??
          (entry.collection?.slug as string | undefined);
        if (collSlug) {
          captureContract = await kernelQuery<CaptureContract | null>(
            "chain.getCaptureContract",
            { collectionSlug: collSlug },
          ) ?? undefined;
        }
      } catch { /* contract fetch is advisory */ }

      // Context-sensitive next: proposal vs. direct commit have different follow-ups
      const next: NextAction[] = isProposal
        ? [
            { tool: "entries", description: "View entry", parameters: { action: "get", entryId } },
          ]
        : [
            { tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId } },
            { tool: "entries", description: "View accepted entry", parameters: { action: "get", entryId } },
          ];

      const summary = isProposal
        ? `Proposal created for ${entryId} (${entry.name}). Awaiting consent.`
        : `Accepted ${entryId} (${entry.name}) onto the Chain.`;

      const commitSuccessEnvelope = success(
        summary,
        {
          entryId,
          name: entry.name,
          outcome: isProposal ? "proposal_created" as const : "committed" as const,
          qualityVerdict: coachingResult?.verdict ?? undefined,
          source: coachingResult?.source ?? undefined,
          contradictions: advisoryWarnings.length,
          ...(epistemic ? { epistemicStatus: epistemic } : {}),
          ...(steeringAdvisory ? { steeringAdvisory } : {}),
          // WP-B (attestation spec §4.3): mirror the auto-verify receipt into the
          // structured payload so --json/agent consumers see it too.
          ...(result?.autoVerified ? { autoVerified: result.autoVerified } : {}),
        },
        next,
      );
      // WP-316 S1a: Attach capture contract so agents know required fields for next capture.
      if (captureContract) {
        commitSuccessEnvelope.contract = captureContract;
      }

      return {
        content: [{ type: "text" as const, text: lines.join("\n") }],
        structuredContent: commitSuccessEnvelope,
      };
}

// WP-484 S3 (clean break, TEN-2157): the standalone `update-entry` /
// `get-history` / `commit-entry` tool registrations retired here.
// handleUpdateEntry / handleGetHistory / handleCommitEntry stay live —
// entries.ts (the `entries` compound) is their only remaining caller.

